import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { ALL_COMPARATORS, type ComparatorType } from '@repo/advanced-filters';
import { notification } from '@repo/ui';
import { LocalStorage } from '@vectord/utils';

const lsInstance = LocalStorage.getInstance('store.tasks.secondaryFilterV1');

export type TimeFilterOptionsType = {
  filterType?: ComparatorType;
  offsetValue?: string;
  fromTime?: string;
  toTime?: string;
};

export enum Entities {
  Task = 'task',
}

export enum AllowedFilterKeys {
  Task = 'task',
  TaskOwner = 'owner',
  TaskStatus = 'status',
  TaskSubject = 'subject',
  TaskDescription = 'description',
  TaskEndTime = 'end_time',
  TaskDateRange = 'date_range',
}

export interface ConditionRHS {
  conditionKey: AllowedFilterKeys;
  conditionComparator: ComparatorType;
  conditionValue: string;
  subConditionKey?: AllowedFilterKeys;
  subConditionComparator?: ComparatorType;
  subConditionValue?: string | TimeFilterOptionsType | string[];
  level: number;
}

export interface ConditionsType {
  [Entities.Task]: Record<string, ConditionRHS>;
}

export interface SecondaryFilterSliceType {
  conditions: Record<string, ConditionsType>;
  sortConfig?: any;
  selectedTaskId?: string;
  searchText: string;
}

export const initialConditions: SecondaryFilterSliceType['conditions'][string] = {
  [Entities.Task]: {
    [`${AllowedFilterKeys.Task}.${AllowedFilterKeys.TaskOwner}`]: {
      conditionKey: AllowedFilterKeys.Task,
      conditionComparator: ALL_COMPARATORS.is,
      conditionValue: 'any',
      subConditionKey: AllowedFilterKeys.TaskOwner,
      subConditionComparator: ALL_COMPARATORS.is,
      subConditionValue: 'any',
      level: 1,
    },
  },
};

const initialState: SecondaryFilterSliceType = {
  conditions: {},
  searchText: '',
  sortConfig: {
    sortField: AllowedFilterKeys.TaskEndTime,
    sortOrder: 'desc',
  },
};

const savedConfig: SecondaryFilterSliceType = lsInstance?.config;

export const secondaryFilterSlice = createSlice({
  name: 'manageTasks/SecondaryFilter',
  initialState: { ...initialState, ...savedConfig },
  reducers: {
    addSecondaryFilter: (state, action: PayloadAction<{
      entityType: Entities,
      value?: ConditionRHS,
      key?: string
    }>) => {
      const taskId = state.selectedTaskId;
      const { entityType, value } = action.payload;
      const objectKey = action.payload.key ?? `${value?.conditionKey}.${value?.subConditionKey ?? ''}`;

      if (!taskId) {
        notification.error({
          message: 'Select a task first',
        });
        return;
      }

      if (!state.conditions[taskId]) {
        state.conditions[taskId] = structuredClone(initialConditions);
      }

      if (value) {
        state.conditions[taskId][entityType][objectKey] = value;
      } else {
        delete state.conditions[taskId][entityType][objectKey];
      }
    },
    removeTimeFilters: (state, action: PayloadAction<{
      entityType: Entities,
    }>) => {
      const taskId = state.selectedTaskId;
      const { entityType } = action.payload;

      if (!taskId) return;

      if (state.conditions[taskId]) {
        state.conditions[taskId][entityType] = Object.fromEntries(
          Object.entries(
            state.conditions[taskId][entityType],
          ).filter(([, condition]) => !(
            typeof condition.subConditionValue === 'object' && 'filterType' in condition.subConditionValue
          )),
        );
      }
    },
    setSelectedTaskId: (state, action: PayloadAction<string>) => {
      state.selectedTaskId = action.payload;
      if (state.conditions[action.payload]) return;

      secondaryFilterSlice.caseReducers.addSecondaryFilter(state, {
        payload: {
          entityType: Entities.Task,
          value: {
            conditionKey: AllowedFilterKeys.Task,
            conditionComparator: ALL_COMPARATORS.is,
            conditionValue: action.payload,
            level: 1,
          },
        },
        type: 'any',
      });
    },
    setSortConfig: (state, action: PayloadAction<{
      sortField?: string,
      sortDirection?: 'ascend' | 'descend',
    }>) => {
      let sortOrder: 'asc' | 'desc' = 'desc';
      if (action.payload.sortDirection === 'ascend') {
        sortOrder = 'asc';
      } else if (action.payload.sortDirection === 'descend') {
        sortOrder = 'desc';
      }
      state.sortConfig = {
        sortField: action.payload.sortField || AllowedFilterKeys.TaskEndTime,
        sortOrder,
      };
    },
    setSearchText: (state, action: PayloadAction<string>) => {
      state.searchText = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      (action) => action.type.startsWith('manageTasks/SecondaryFilter/'),
      (state) => { lsInstance.config = state; },
    );
  },
});

export const {
  addSecondaryFilter,
  removeTimeFilters,
  setSelectedTaskId,
  setSortConfig,
  setSearchText,
} = secondaryFilterSlice.actions;

export default secondaryFilterSlice.reducer;
